import React from 'react';
import classNames from 'classnames';
import { ShieldCheck } from 'lucide-react';

import Field from '../../PageBuilder/Field';

import blogScrap from '../../../assets/landing/blog-scrap.jpg';
import blogDiscarded from '../../../assets/landing/blog-discarded.jpg';

import css from './BlogPreviewSection.module.css';

const FALLBACK_POSTS = [
  {
    id: 'blog-1',
    slug: 'cost-of-scrap',
    badge: 'Industry Insights',
    badgeVariant: 'primary',
    image: blogScrap,
    imageAlt: 'Construction scrap',
    title: 'How Much Money Are You Throwing Away Every Month on Scrap Material?',
    excerpt:
      'Recent studies show that up to 30% of total weight of building materials delivered to a construction site ends up as waste. Discover the hidden economic impact of dumpster fees and discarded usable materials on your bottom line.',
  },
  {
    id: 'blog-2',
    slug: 'top-5-discarded',
    badge: 'Money Makers',
    badgeVariant: 'secondary',
    image: blogDiscarded,
    imageAlt: 'Discarded materials',
    title: '5 Things Contractors & DIYers Discard That Are Worth Cash Today',
    excerpt:
      'Before you order that roll-off dumpster, take a second look at your pile. From dimensional lumber offcuts to copper wire and perfectly good architectural fixtures, learn which "trash" items are in high demand right now.',
  },
  {
    id: 'blog-3',
    slug: 'safe-in-person-transactions',
    badge: 'Safety Guide',
    badgeVariant: 'primary',
    iconOnly: true,
    title: 'The Complete Guide to Safe In-Person Transactions',
    excerpt:
      'Learn how to plan safer meetups, inspect materials with confidence, and use escrow correctly so every local pickup stays smooth and protected.',
  },
];

type BlogBlock = {
  blockId?: string;
  title?: { content?: string } | null;
  text?: { content?: string } | null;
  callToAction?: { href?: string; content?: string } | null;
};

type BlogPreviewSectionProps = {
  sectionId: string;
  className?: string;
  title?: { content?: string } | null;
  description?: { content?: string } | null;
  callToAction?: { fieldType?: string; href?: string; content?: string } | null;
  blocks?: BlogBlock[];
  options?: any;
};

/**
 * Blog Preview: Console blocks override the seeded fallback posts. The
 * fallback's third entry uses the icon-card variant (gradient + ShieldCheck).
 */
const BlogPreviewSection = (props: BlogPreviewSectionProps) => {
  const { sectionId, className, title, blocks, callToAction } = props;
  const isExternalCta = callToAction?.fieldType === 'externalButtonLink';
  const ctaArrow = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );

  const hasTitle = title?.content;
  const hasBlocks = blocks && blocks.length > 0;

  return (
    <section id={sectionId} className={classNames(css.root, className)}>
      <div className={css.container}>
        <div className={css.headerRow}>
          <div className={css.headerLeft}>
            {hasTitle ? (
              <Field data={title} className={css.sectionTitle} />
            ) : (
              <h2 className={css.sectionTitle}>
                <svg className={css.bookIcon} width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                </svg>
                The Scrap Yard Blog
              </h2>
            )}
          </div>
          {callToAction?.href && (
            <a
              href={callToAction.href}
              className={css.viewAllLink}
              {...(isExternalCta ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
              {callToAction.content || 'View All Posts'}
              {ctaArrow}
            </a>
          )}
        </div>

        <div className={css.grid}>
          {((hasBlocks ? blocks : FALLBACK_POSTS) as any[]).map((post: any, idx: number) => {
            // Console block shape: { blockId, title: {content}, text: {content}, callToAction: {href, content} }
            // Fallback shape: { id, image, badge, title, excerpt }
            const isConsoleBlock = hasBlocks;
            const key = isConsoleBlock ? (post.blockId || idx) : post.id;
            const cardTitle = isConsoleBlock ? post.title?.content : post.title;
            const cardExcerpt = isConsoleBlock ? post.text?.content : post.excerpt;
            const cardHref = isConsoleBlock ? post.callToAction?.href : null;
            const cardImage = isConsoleBlock ? null : post.image;
            const cardImageAlt = isConsoleBlock ? cardTitle : post.imageAlt;
            const isIconCard = !isConsoleBlock && post.iconOnly;

            const cardContent = (
              <>
                {isIconCard ? (
                  <div className={css.iconCardWrapper}>
                    <ShieldCheck className={css.iconCardIcon} />
                    <div className={css.imageBadgeWrapper}>
                      <span className={classNames(css.imageBadge, css.badgePrimary)}>
                        {post.badge}
                      </span>
                    </div>
                  </div>
                ) : cardImage ? (
                  <div className={css.imageWrapper}>
                    <img src={cardImage} alt={cardImageAlt} className={css.image} />
                    <div className={css.imageBadgeWrapper}>
                      <span className={classNames(css.imageBadge, css.badgePrimary)}>
                        {post.badge}
                      </span>
                    </div>
                  </div>
                ) : null}
                <div className={css.cardBody}>
                  <h3 className={css.cardTitle}>{cardTitle}</h3>
                  <p className={css.cardExcerpt}>{cardExcerpt}</p>
                  <div className={css.readMore}>
                    {isConsoleBlock ? (post.callToAction?.content || 'Read More') : 'Read Full Article'}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </div>
                </div>
              </>
            );

            return cardHref ? (
              <a key={key} href={cardHref} className={css.card}>
                {cardContent}
              </a>
            ) : (
              <div key={key} className={css.card}>
                {cardContent}
              </div>
            );
          })}
        </div>

        {callToAction?.href && (
          <div className={css.mobileViewAll}>
            <a
              href={callToAction.href}
              className={css.mobileViewAllButton}
              {...(isExternalCta ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
              {callToAction.content || 'View All Posts'}
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogPreviewSection;
